import { useState, useMemo, useEffect } from "react";
import { useTenant } from "../../../../hooks/useTenant";
import { db } from "../../../../services/firebase";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { COLLECTIONS } from "../../../../services/collections";
import { FINANCIAL_STATUS, FINANCIAL_TYPES } from "../../../../constants/financial";

const todayStr = () => new Date().toISOString().split("T")[0];

const getFutureDate = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().split("T")[0];
};

/**
 * useCashFlow
 * Fluxo de Caixa completo — mostra movimentos realizados (PAID) e projetados (PENDING/OVERDUE).
 * Utiliza duas queries Firestore:
 *   1. PENDING/OVERDUE com dueDate no período
 *   2. PAID com paymentDate no período
 */
export const useCashFlow = () => {
    const { idTenant } = useTenant();

    const [startDate, setStartDate] = useState(todayStr());
    const [endDate, setEndDate] = useState(getFutureDate(30));

    const [bankAccounts, setBankAccounts] = useState([]);
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);

    // Saldo Consolidado Atual (soma de todas as contas)
    const currentTotalBalance = useMemo(() => {
        return bankAccounts.reduce((acc, bank) => acc + (bank.balance || 0), 0);
    }, [bankAccounts]);

    useEffect(() => {
        if (!idTenant) return;

        let isMounted = true;
        setLoading(true);

        const fetchData = async () => {
            try {
                // 1. Busca contas bancárias
                const snapBanks = await getDocs(
                    collection(db, "tenants", idTenant, COLLECTIONS.BANK_ACCOUNTS)
                );
                if (!isMounted) return;

                const banks = [];
                snapBanks.forEach(doc => banks.push({ id: doc.id, ...doc.data() }));
                setBankAccounts(banks);

                const recordsRef = collection(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS);

                // 2a. Busca PENDING/OVERDUE com dueDate no período
                const qPending = query(
                    recordsRef,
                    where("status", "in", [FINANCIAL_STATUS.PENDING, FINANCIAL_STATUS.OVERDUE]),
                    where("dueDate", ">=", startDate),
                    where("dueDate", "<=", endDate),
                    orderBy("dueDate", "asc")
                );

                // 2b. Busca PAID com paymentDate no período
                const qPaid = query(
                    recordsRef,
                    where("status", "==", FINANCIAL_STATUS.PAID),
                    where("paymentDate", ">=", startDate),
                    where("paymentDate", "<=", endDate),
                    orderBy("paymentDate", "asc")
                );

                // 2c. Busca OVERDUE anteriores ao período (atrasados antigos devem aparecer)
                const qOverdueOld = query(
                    recordsRef,
                    where("status", "in", [FINANCIAL_STATUS.PENDING, FINANCIAL_STATUS.OVERDUE]),
                    where("dueDate", "<", startDate),
                    orderBy("dueDate", "asc")
                );

                const [snapPending, snapPaid, snapOverdueOld] = await Promise.all([
                    getDocs(qPending),
                    getDocs(qPaid),
                    getDocs(qOverdueOld),
                ]);

                if (!isMounted) return;

                const allRecords = new Map();

                // Consolidar resultados (Map evita duplicatas pelo id)
                const addSnap = (snap) => {
                    snap.forEach(doc => {
                        if (!allRecords.has(doc.id)) {
                            allRecords.set(doc.id, { id: doc.id, ...doc.data() });
                        }
                    });
                };

                addSnap(snapPending);
                addSnap(snapPaid);
                addSnap(snapOverdueOld);

                setRecords(Array.from(allRecords.values()));
            } catch (error) {
                console.error("[useCashFlow] Erro ao carregar dados:", error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchData();

        return () => { isMounted = false; };
    }, [idTenant, startDate, endDate]);

    // Agrupar registros por dia e calcular progressão de saldo
    const projectedDays = useMemo(() => {
        if (!records.length) return [];

        const today = todayStr();
        const grouped = {};

        // Grupo especial para atrasados/vencimentos antigos
        const overdueKey = 'OVERDUE_OR_TODAY';

        records.forEach(r => {
            // Data efetiva: paymentDate para PAID, dueDate para PENDING/OVERDUE
            const effectiveDate = r.status === FINANCIAL_STATUS.PAID
                ? (r.paymentDate || r.dueDate || today)
                : (r.dueDate || today);

            // Atrasados ou vence hoje → agrupa no bloco especial
            const isRetroactive = effectiveDate < startDate;
            const dateKey = isRetroactive ? overdueKey : effectiveDate;

            if (!grouped[dateKey]) {
                grouped[dateKey] = {
                    date: dateKey,
                    displayDate: dateKey === overdueKey
                        ? 'Atrasados / Anteriores'
                        : dateKey.split('-').reverse().join('/'),
                    income: 0,
                    expense: 0,
                    paidIncome: 0,
                    paidExpense: 0,
                    records: [],
                    isOverdue: dateKey === overdueKey
                };
            }

            grouped[dateKey].records.push(r);

            const amount = r.netAmount || 0;
            if (r.type === FINANCIAL_TYPES.INCOME) {
                grouped[dateKey].income += amount;
                if (r.status === FINANCIAL_STATUS.PAID) grouped[dateKey].paidIncome += amount;
            }
            if (r.type === FINANCIAL_TYPES.EXPENSE) {
                grouped[dateKey].expense += amount;
                if (r.status === FINANCIAL_STATUS.PAID) grouped[dateKey].paidExpense += amount;
            }
        });

        // Remove grupo especial se vazio
        if (grouped[overdueKey] && grouped[overdueKey].records.length === 0) {
            delete grouped[overdueKey];
        }

        // Ordenar chaves
        const sortedKeys = Object.keys(grouped).sort((a, b) => {
            if (a === overdueKey) return -1;
            if (b === overdueKey) return 1;
            return a.localeCompare(b);
        });

        const timeline = [];
        let runningBalance = currentTotalBalance;

        sortedKeys.forEach(key => {
            const dayData = grouped[key];
            // Apenas PENDING/OVERDUE afetam a projeção (PAID já está no saldo do banco)
            const pendingNet = (dayData.income - dayData.paidIncome) - (dayData.expense - dayData.paidExpense);
            runningBalance += pendingNet;

            timeline.push({
                ...dayData,
                netDay: dayData.income - dayData.expense,
                projectedBalance: runningBalance
            });
        });

        return timeline;
    }, [records, currentTotalBalance, startDate]);

    return {
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        bankAccounts,
        currentTotalBalance,
        projectedDays,
        records,
        loading,
    };
};
